/*
 * A component which renders the Khan Academy logo which resides in the top
 * header bar.
 */
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Clickable from "@khanacademy/wonder-blocks-clickable";
import type {StyleType} from "@khanacademy/wonder-blocks-core";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {
    markMinorConversions,
    type MinorConversionId,
} from "@khan/analytics-learning-time";

import Logo from "./logo.tsx";

type DefaultProps = {
    // Modifies logo transparency depending on the header background color
    backgroundColor: "white" | "darkBlue";
    // Optional: If specified, sets the logo image color. Defaults to #14bf96.
    logoColor?: string;
    // Optional: If specified, sets the logo text color. Defaults to #0a2a66.
    logoTextColor?: string;
};

type Props = DefaultProps & {
    // Extra attributes to be applied to the logo link
    linkAttributes?: Record<string, any>;
    // Extra styles to be applied to the logo.
    logoStyles?: StyleType;
    // A optional big-bingo conversion to mark when the component is clicked.
    conversion?: MinorConversionId;
};

class HeaderLogo extends React.Component<Props> {
    static defaultProps: DefaultProps = {
        backgroundColor: "white",
        logoColor: "#14bf96",
        logoTextColor: "#0a2a66",
    };

    render(): React.ReactNode {
        const {conversion, logoColor, logoTextColor} = this.props;
        const isTransparent = this.props.backgroundColor !== "white";

        return (
            <Clickable
                testId="header-logo"
                href="/"
                aria-label="Khan Academy"
                style={[
                    this.props.logoStyles,
                    {color: isTransparent ? color.white : logoTextColor},
                    styles.link,
                    isTransparent && styles.transparent,
                ]}
                safeWithNav={async () => {
                    if (conversion) {
                        await markMinorConversions([
                            {
                                id: conversion,
                            },
                        ]);
                    }
                }}
            >
                {() => <Logo fillColor={logoColor} />}
            </Clickable>
        );
    }
}

const styles = StyleSheet.create({
    link: {
        ":hover": {
            color: color.blue,
        },

        ":focus": {
            outline: `4px solid ${color.blue}`,
        },
    },

    transparent: {
        "@media print": {
            // Printable reports should always show logo with dark blue text
            color: "#0a2a66",
        },
    },
});

export default HeaderLogo;
